import { Component } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { lastValueFrom } from 'rxjs';
import {
  GenericMasterData,
  LyfisMasterDataResponse,
  MasterDataResponse,
} from '../../../models/masterDataResponse';
import { MasterdataService } from '../../../services/masterdata.service';

export interface MasterDataItemDoc extends GenericMasterData {
  [key: string]: unknown;
}

interface SearchResult {
  masterDataIndex: number;
  masterData: MasterDataItemDoc;
  masterDataType: 'COMMON_MASTER_DATA' | 'LYFIS_MASTER_DATA' | null;
  masterDataCategory: string;
}

@Component({
  selector: 'app-search-master-data',
  templateUrl: './search-master-data.component.html',
  styleUrls: ['./search-master-data.component.scss'],
})
export class SearchMasterDataComponent extends AnyoComponent {
  mId = '';
  loading = false;
  searched = false;

  masterData: MasterDataResponse | null = null;
  lyfisData: LyfisMasterDataResponse | null = null;
  masterDataType: 'COMMON_MASTER_DATA' | 'LYFIS_MASTER_DATA' | null = null;

  result: SearchResult | null = null;

  constructor(
    protected override auth: AuthService,
    private masterDataService: MasterdataService,
    private message: NzMessageService,
  ) {
    super(auth);
  }

  async ready(): Promise<void> {
    this.loading = true;
    try {
      const [master, lyfis] = await Promise.all([
        lastValueFrom(this.masterDataService.masterDataList()),
        lastValueFrom(this.masterDataService.lyfisMasterDataList()),
      ]);
      this.masterData = master;
      this.lyfisData = lyfis;
    } catch (error) {
      this.message.error('Failed to pre-fetch master data');
    } finally {
      this.loading = false;
    }
  }

  searchMasterData(): void {
    const searchTerm = this.mId.trim();
    if (!searchTerm) {
      this.message.warning('m_id is required');
      return;
    }

    if (!this.masterData || !this.lyfisData) {
      this.message.warning('Master data is still loading or failed to load');
      return;
    }

    this.searched = false;
    this.result = null;

    let foundItem: MasterDataItemDoc | null = null;
    let foundCategory = '';
    let foundIndex = -1;
    const hasMId = (item: unknown): item is MasterDataItemDoc =>
      !!item &&
      typeof item === 'object' &&
      'm_id' in item &&
      (item as MasterDataItemDoc).m_id === searchTerm;

    const masterKeys = Object.keys(
      this.masterData,
    ) as (keyof MasterDataResponse)[];
    for (const category of masterKeys) {
      const items = this.masterData[category];
      if (Array.isArray(items)) {
        const index = items.findIndex(hasMId);
        if (index !== -1) {
          foundItem = items[index] as MasterDataItemDoc;
          foundCategory = category;
          foundIndex = index;
          this.masterDataType = 'COMMON_MASTER_DATA';
          break;
        }
      }
    }

    if (!foundItem) {
      const lyfisKeys = Object.keys(
        this.lyfisData,
      ) as (keyof LyfisMasterDataResponse)[];
      for (const category of lyfisKeys) {
        const items = this.lyfisData[category];
        if (Array.isArray(items)) {
          const index = items.findIndex(hasMId);
          if (index !== -1) {
            foundItem = items[index] as unknown as MasterDataItemDoc;
            foundCategory = category;
            foundIndex = index;
            this.masterDataType = 'LYFIS_MASTER_DATA';
            break;
          }
        }
      }
    }

    if (foundItem) {
      this.result = {
        masterDataCategory: foundCategory,
        masterDataIndex: foundIndex,
        masterData: foundItem,
        masterDataType: this.masterDataType,
      };
      this.searched = true;
    } else {
      this.message.error('No master data found');
    }
  }

  clear(): void {
    this.mId = '';
    this.result = null;
    this.searched = false;
  }
}
